/**
 * DatePresetFilter — Phase R10.3 (2026-07-04)
 *
 * Preset dropdown with 6 options: Today / Yesterday / Last 7 Days /
 * Last 30 Days / This Month / Custom Range. Translates presets into
 * ISO date strings that the backend `/counts`, `/branch-performance`,
 * and `/receipt-claims` endpoints consume as `date_from=&date_to=`.
 *
 * Props:
 *   value: { preset: string, date_from?: string, date_to?: string }
 *   onChange(next): called when user picks a preset or saves a custom range
 */
import { useRef, useState, useEffect } from 'react';
import { ChevronDown, Calendar, Check } from 'lucide-react';

const PRESETS = [
  { key: 'all',           label: 'All Time' },
  { key: 'today',         label: 'Today' },
  { key: 'yesterday',     label: 'Yesterday' },
  { key: 'last_7_days',   label: 'Last 7 Days' },
  { key: 'last_30_days',  label: 'Last 30 Days' },
  { key: 'this_month',    label: 'This Month' },
  { key: 'custom',        label: 'Custom Range' },
];

function toIso(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function presetToDates(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today':         return { date_from: toIso(today),            date_to: toIso(today) };
    case 'yesterday':     { const y = new Date(today); y.setDate(y.getDate() - 1); return { date_from: toIso(y), date_to: toIso(y) }; }
    case 'last_7_days':   { const s = new Date(today); s.setDate(s.getDate() - 6); return { date_from: toIso(s), date_to: toIso(today) }; }
    case 'last_30_days':  { const s = new Date(today); s.setDate(s.getDate() - 29); return { date_from: toIso(s), date_to: toIso(today) }; }
    case 'this_month':    { const s = new Date(today.getFullYear(), today.getMonth(), 1); return { date_from: toIso(s), date_to: toIso(today) }; }
    default:              return { date_from: null, date_to: null };
  }
}

export default function DatePresetFilter({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(value?.date_from ?? '');
  const [customTo,   setCustomTo]   = useState(value?.date_to ?? '');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setCustomOpen(false);
      }
    };
    if (open || customOpen) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open, customOpen]);

  const currentPreset = value?.preset ?? 'all';
  const currentLabel = PRESETS.find((p) => p.key === currentPreset)?.label ?? 'All Time';

  const pick = (key) => {
    if (key === 'custom') {
      setCustomOpen(true);
      setOpen(false);
      return;
    }
    const { date_from, date_to } = presetToDates(key);
    onChange({ preset: key, date_from, date_to });
    setOpen(false);
  };

  const saveCustom = () => {
    if (!customFrom || !customTo) return;
    onChange({ preset: 'custom', date_from: customFrom, date_to: customTo });
    setCustomOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef} data-testid="date-preset-filter">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setCustomOpen(false); }}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-medium bg-[#15151B] border-white/10 text-white/90 hover:border-white/25 min-h-[40px]"
        data-testid="date-preset-filter-trigger"
      >
        <Calendar size={14} className="text-gold" />
        <span>{currentLabel}</span>
        {currentPreset === 'custom' && value?.date_from ? (
          <span className="text-[11px] text-white/50 font-mono">
            {value.date_from} → {value.date_to}
          </span>
        ) : null}
        <ChevronDown size={14} className={`text-white/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div
          className="absolute left-0 mt-2 w-56 rounded-2xl border border-white/10 bg-[#15151B] shadow-xl z-40"
          data-testid="date-preset-filter-panel"
        >
          {PRESETS.map((p) => {
            const active = p.key === currentPreset;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => pick(p.key)}
                data-testid={`date-preset-filter-option-${p.key}`}
                className={
                  'w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/[0.05] ' +
                  (active ? 'text-gold' : 'text-white/80')
                }
              >
                <span>{p.label}</span>
                {active ? <Check size={12} className="text-gold" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {customOpen ? (
        <div
          className="absolute left-0 mt-2 w-72 rounded-2xl border border-white/10 bg-[#15151B] shadow-xl p-4 z-40"
          data-testid="date-preset-filter-custom-panel"
        >
          <div className="text-xs text-white/60 mb-2">Custom Date Range</div>
          <label className="block text-[11px] text-white/50 mb-1">From</label>
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className="w-full mb-3 px-2 py-1.5 rounded-lg bg-[#0B0B0F] border border-white/10 text-sm text-white"
            data-testid="date-preset-filter-custom-from"
          />
          <label className="block text-[11px] text-white/50 mb-1">To</label>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className="w-full mb-3 px-2 py-1.5 rounded-lg bg-[#0B0B0F] border border-white/10 text-sm text-white"
            data-testid="date-preset-filter-custom-to"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setCustomOpen(false)}
              className="px-3 py-1.5 text-xs rounded-lg text-white/60 hover:text-white"
              data-testid="date-preset-filter-custom-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveCustom}
              disabled={!customFrom || !customTo}
              className="px-3 py-1.5 text-xs rounded-lg bg-gold text-black font-semibold disabled:opacity-50"
              data-testid="date-preset-filter-custom-save"
            >
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
